import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  ClerkTokenUnavailableError,
  clerkTokenUnavailableErrorCode,
  forceRefreshClerkTokenForApi,
  getClerkTokenForApi,
  hasClerkTokenProvider,
  isInvalidOrExpiredClerkTokenResponseData,
} from './clerkTokenProvider';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string = '000000',
    public reason: string | string[] | null = null,
  ) {
    const message = Array.isArray(reason)
      ? reason.join(', ')
      : reason || 'An error occurred interacting with the API.';
    super(message);
    this.name = 'ApiClientError';

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiClientError);
    }
  }
}

const MAX_PLAIN_TEXT_REASON = 200;

/**
 * A body that is itself the reason: a short line of plain text, as a proxy or a framework default
 * sends ("Gateway Timeout"). A long body or one with markup is an error page, not a sentence to show.
 */
function plainTextReason(data: unknown): string | null {
  if (typeof data !== 'string') return null;
  const text = data.trim();
  if (!text || text.length > MAX_PLAIN_TEXT_REASON || /[<>]/.test(text)) return null;
  return text;
}

export interface ApiClientConfig extends AxiosRequestConfig {
  baseURL: string;
  onUnauthorized?: () => void;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retriedAfter401?: boolean;
}

export class ApiClient {
  private client: AxiosInstance;
  private onUnauthorized?: () => void;

  constructor(config: ApiClientConfig) {
    const { onUnauthorized, ...axiosConfig } = config;

    this.onUnauthorized = onUnauthorized;
    this.client = axios.create({
      timeout: 30000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      ...axiosConfig,
    });

    this.client.interceptors.request.use(async config => {
      const retryConfig = config as RetryableRequestConfig;
      if (hasClerkTokenProvider() && !retryConfig._retriedAfter401) {
        const freshToken = await getClerkTokenForApi();
        if (freshToken) {
          config.headers['Authorization'] = `Bearer ${freshToken}`;
        }
      }

      const roleOverride = localStorage.getItem('waterlily-role-override');
      if (roleOverride) {
        config.headers['X-Waterlily-Role'] = roleOverride;
      }

      const impersonateAdvisorId = localStorage.getItem('waterlily-impersonate-advisor-id');
      if (impersonateAdvisorId) {
        config.headers['x-impersonate-advisor-id'] = impersonateAdvisorId;
      }

      return config;
    });

    // The bearer token can go stale between when we fetched it and when the server
    // actually processes the request. Retry once with a forcibly-refreshed token before
    // letting a 401 propagate to handleError() (which treats it as a real logout).
    this.client.interceptors.response.use(
      response => response,
      async (error: unknown) => {
        const config = axios.isAxiosError(error)
          ? (error.config as RetryableRequestConfig)
          : undefined;

        if (
          axios.isAxiosError(error) &&
          error.response?.status === 401 &&
          isInvalidOrExpiredClerkTokenResponseData(error.response.data) &&
          config &&
          !config._retriedAfter401 &&
          hasClerkTokenProvider()
        ) {
          const freshToken = await forceRefreshClerkTokenForApi();
          if (freshToken) {
            config._retriedAfter401 = true;
            config.headers.set('Authorization', `Bearer ${freshToken}`);
            return this.client.request(config);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  setToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }

  async get<T = any>(
    path: string,
    params?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response = await this.client.get<T>(path, { params, ...config });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async post<T = any>(
    path: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response = await this.client.post<T>(path, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async put<T = any>(
    path: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response = await this.client.put<T>(path, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete<T = any>(
    path: string,
    params?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response = await this.client.delete<T>(path, { params, ...config });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): ApiClientError {
    if (error instanceof ClerkTokenUnavailableError) {
      return new ApiClientError(503, clerkTokenUnavailableErrorCode);
    }

    if (axios.isAxiosError(error) && error.response) {
      const { data, status } = error.response;

      if (status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }

      if (data) {
        const code = data.code || '000000';
        // The code is never the reason: with none found, the error keeps its own default wording.
        const reason =
          (typeof data.errorMessage === 'string' ? data.errorMessage : null) ||
          (typeof data.error === 'string' ? data.error : null) ||
          data.errors ||
          data.message ||
          plainTextReason(data);
        return new ApiClientError(status, code, reason);
      }
      return new ApiClientError(status);
    }
    throw error;
  }
}
